import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Download, Search, RotateCcw, Loader2, Filter, IndianRupee, TrendingUp,
  TrendingDown, FileDown, Wallet, AlertTriangle, Calendar,
} from "lucide-react";
import { useCustomerAuth, customerFetch } from "./CustomerAuthContext";
import { API_BASE } from "../DataContext";

const inr = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
const fmtDate = (iso) => { if (!iso) return "—"; const [y,m,d]=String(iso).split("-"); return y&&m&&d?`${d}-${m}-${y}`:iso; };

const EMPTY = { q: "", voucher_type: "All", date_from: "", date_to: "", unpaid_only: false };
const PAGE_SIZE = 50;

export default function CustomerStatement() {
  const { token } = useCustomerAuth();
  const [draft, setDraft] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [summary, setSummary] = useState(null);
  const [txns, setTxns] = useState(null);
  const [loading, setLoading] = useState(false);
  const [vchTypes, setVchTypes] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  const fetchAll = useCallback(async (filters, pageNum) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        q: filters.q, voucher_type: filters.voucher_type,
        date_from: filters.date_from, date_to: filters.date_to,
        unpaid_only: filters.unpaid_only ? "1" : "0",
        page: String(pageNum), page_size: String(PAGE_SIZE),
      });
      const sParams = new URLSearchParams({
        date_from: filters.date_from, date_to: filters.date_to,
      });
      const [s, t] = await Promise.all([
        customerFetch(token)(`/api/customer/ledger/summary?${sParams.toString()}`).then((r) => r.json()),
        customerFetch(token)(`/api/customer/ledger/transactions?${params.toString()}`).then((r) => r.json()),
      ]);
      setSummary(s);
      setTxns(t);
    } catch {
      setTxns({ items: [], total: 0, total_pages: 1, page: 1, opening_balance: 0 });
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    customerFetch(token)("/api/customer/ledger/voucher-types")
      .then((r) => r.json()).then((d) => setVchTypes(d.voucher_types || [])).catch(() => setVchTypes([]));
  }, [token]);

  useEffect(() => { fetchAll(applied, page); }, [applied, page, fetchAll]);

  const applyFilters = () => { setApplied(draft); setPage(1); };
  const clearFilters = () => { setDraft(EMPTY); setApplied(EMPTY); setPage(1); };

  const items = txns?.items || [];
  const total = txns?.total ?? 0;
  const totalPages = txns?.total_pages ?? 1;

  const activeCount = useMemo(() => {
    let n = 0;
    if (applied.q) n++; if (applied.voucher_type && applied.voucher_type !== "All") n++;
    if (applied.date_from) n++; if (applied.date_to) n++; if (applied.unpaid_only) n++;
    return n;
  }, [applied]);

  const downloadStatement = async (preset, format) => {
    setDownloading(true);
    try {
      const p = new URLSearchParams({ format, preset });
      if (preset === "custom") {
        if (!applied.date_from || !applied.date_to) return alert("Set From/To dates first, then click Fetch, then download Custom.");
        p.set("date_from", applied.date_from);
        p.set("date_to", applied.date_to);
      }
      const r = await fetch(`${API_BASE}/api/customer/ledger/download?${p.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("Download failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `BAISHALII-Statement-${new Date().toISOString().slice(0,10)}.${format === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setShowDownloadMenu(false);
    } catch (e) { alert(e.message); }
    finally { setDownloading(false); }
  };

  const cards = summary ? [
    { key: "opening_balance", label: "Opening Balance", value: summary.opening_balance, icon: Calendar, color: "bg-slate-100 text-slate-700" },
    { key: "total_sales", label: "Total Sales", value: summary.total_sales, icon: TrendingUp, color: "bg-emerald-100 text-emerald-700" },
    { key: "total_receipts", label: "Total Receipts", value: summary.total_receipts, icon: TrendingDown, color: "bg-sky-100 text-sky-700" },
    { key: "credit_notes", label: "Credit Notes", value: summary.credit_notes, icon: IndianRupee, color: "bg-amber-100 text-amber-700" },
    { key: "debit_notes", label: "Debit Notes", value: summary.debit_notes, icon: IndianRupee, color: "bg-orange-100 text-orange-700" },
    { key: "closing_balance", label: "Closing Balance", value: summary.closing_balance, icon: Wallet, color: "bg-violet-100 text-violet-700", bold: true },
    { key: "outstanding_balance", label: "Outstanding", value: summary.outstanding_balance, icon: Wallet, color: "bg-red-50 text-red-700" },
    { key: "overdue_balance", label: "Overdue", value: summary.overdue_balance, icon: AlertTriangle, color: "bg-red-100 text-red-700" },
  ] : [];

  return (
    <div className="space-y-5" data-testid="customer-statement">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-[#0a2a5e]">Account Statement</h1>
          {summary && (summary.period_from || summary.last_updated) && (
            <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
              {summary.period_from && summary.period_to && (
                <span><span className="font-semibold text-slate-700">Statement Period:</span> {fmtDate(summary.period_from)} to {fmtDate(summary.period_to)}</span>
              )}
              {summary.last_updated && (
                <span><span className="font-semibold text-slate-700">Last Updated:</span> {String(summary.last_updated).slice(0,10).split('-').reverse().join('-')}</span>
              )}
            </div>
          )}
        </div>
        <div className="relative">
          <button
            onClick={() => setShowDownloadMenu((v) => !v)} disabled={downloading}
            data-testid="download-menu-btn"
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#0a2a5e] px-3 py-2 text-sm font-semibold text-white hover:bg-[#0a2a5e]/90 disabled:opacity-40"
          >
            <FileDown className="h-4 w-4" /> {downloading ? "Preparing…" : "Download Statement"}
          </button>
          {showDownloadMenu && (
            <div className="absolute right-0 mt-2 w-72 bg-white border border-slate-200 rounded-xl shadow-lg z-20 p-2" data-testid="download-menu">
              {[
                { key: "current-month", label: "Current Month" },
                { key: "previous-month", label: "Previous Month" },
                { key: "custom", label: "Custom Date Range (uses applied filters)" },
                { key: "financial-year", label: "Financial Year" },
                { key: "complete", label: "Complete Ledger" },
              ].map((p) => (
                <div key={p.key} className="flex items-center justify-between px-2 py-1.5 hover:bg-slate-50 rounded">
                  <span className="text-sm text-slate-700">{p.label}</span>
                  <div className="flex gap-1">
                    <button onClick={() => downloadStatement(p.key, "pdf")} className="text-xs font-semibold text-[#0a2a5e] hover:underline px-2 py-1 rounded" data-testid={`dl-${p.key}-pdf`}>PDF</button>
                    <button onClick={() => downloadStatement(p.key, "excel")} className="text-xs font-semibold text-emerald-700 hover:underline px-2 py-1 rounded" data-testid={`dl-${p.key}-excel`}>Excel</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="statement-cards">
        {!summary && [...Array(8)].map((_, i) => (<div key={i} className="h-24 bg-slate-100 animate-pulse rounded-xl" />))}
        {cards.map((c) => (
          <div key={c.key} className={`bg-white rounded-2xl border border-slate-100 p-4 shadow-sm ${c.bold ? "ring-2 ring-[#0a2a5e]/10" : ""}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-slate-500 font-semibold">{c.label}</span>
              <div className={`h-8 w-8 rounded-lg ${c.color} flex items-center justify-center`}><c.icon className="h-4 w-4" /></div>
            </div>
            <div className={`text-lg font-black ${c.bold ? "text-[#0a2a5e]" : "text-slate-800"}`}>₹ {inr(c.value)}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-5 space-y-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="text" placeholder="Search voucher no, particulars, narration…" value={draft.q}
              onChange={(e) => setDraft({ ...draft, q: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && applyFilters()}
              data-testid="stmt-search"
              className="w-full rounded-lg border border-slate-200 pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a2a5e]/20 focus:border-[#0a2a5e]" />
          </div>
          {activeCount > 0 && <span className="hidden md:inline-flex items-center gap-1 text-xs text-slate-500"><Filter className="h-3 w-3" /> {activeCount} active</span>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">From</label>
            <input type="date" value={draft.date_from} onChange={(e) => setDraft({ ...draft, date_from: e.target.value })} data-testid="stmt-date-from" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">To</label>
            <input type="date" value={draft.date_to} onChange={(e) => setDraft({ ...draft, date_to: e.target.value })} data-testid="stmt-date-to" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Voucher Type</label>
            <select value={draft.voucher_type} onChange={(e) => setDraft({ ...draft, voucher_type: e.target.value })} data-testid="stmt-vch-type" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white">
              <option value="All">All</option>
              {vchTypes.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
              <input type="checkbox" checked={draft.unpaid_only} onChange={(e) => setDraft({ ...draft, unpaid_only: e.target.checked })}
                data-testid="stmt-unpaid-only" className="h-4 w-4 accent-[#0a2a5e]" />
              Unpaid invoices only
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={clearFilters} data-testid="stmt-clear-filters"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"><RotateCcw className="h-4 w-4" /> Clear</button>
          <button onClick={applyFilters} data-testid="stmt-fetch"
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#0a2a5e] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0a2a5e]/90"><Search className="h-4 w-4" /> Fetch</button>
        </div>
      </div>

      {/* Transactions table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3 font-semibold">Date</th>
              <th className="px-4 py-3 font-semibold">Description</th>
              <th className="px-4 py-3 font-semibold">Voucher Type</th>
              <th className="px-4 py-3 font-semibold">Voucher No</th>
              {summary?.has_reference && <th className="px-4 py-3 font-semibold">Reference</th>}
              <th className="px-4 py-3 font-semibold text-right">Debit</th>
              <th className="px-4 py-3 font-semibold text-right">Credit</th>
              <th className="px-4 py-3 font-semibold text-right">Balance</th>
              {summary?.has_due_date && <th className="px-4 py-3 font-semibold">Due Date</th>}
              {summary?.has_due_date && <th className="px-4 py-3 font-semibold text-right">Days O/S</th>}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400"><Loader2 className="h-4 w-4 inline animate-spin mr-2" />Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-12 text-center text-slate-400 text-sm">
                {activeCount > 0 ? <>No matching transactions. <button onClick={clearFilters} className="text-[#0a2a5e] hover:underline font-semibold">Clear filters</button></>
                  : "No transactions yet. Your ledger will appear here once your BAISHALII accounts team uploads it."}
              </td></tr>
            )}
            {!loading && items.map((t, i) => (
              <tr key={t.id || i} className="border-t border-slate-100 hover:bg-sky-50/50">
                <td className="px-4 py-3 whitespace-nowrap text-slate-600">{fmtDate(t.txn_date)}</td>
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-800">
                    {t.direction && <span className="text-slate-400 font-normal mr-1">{t.direction}</span>}
                    {t.ledger_name || t.particulars || "—"}
                  </div>
                  {t.narration && <div className="text-xs text-slate-500 truncate max-w-md">{t.narration}</div>}
                </td>
                <td className="px-4 py-3"><span className="inline-block px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 text-xs font-semibold">{t.voucher_type || "—"}</span></td>
                <td className="px-4 py-3 font-mono text-xs">{t.voucher_number || <span className="text-slate-400">—</span>}</td>
                {summary?.has_reference && <td className="px-4 py-3 font-mono text-xs">{t.reference || <span className="text-slate-300">—</span>}</td>}
                <td className={`px-4 py-3 text-right whitespace-nowrap ${t.debit ? "text-slate-800 font-semibold" : "text-slate-300"}`}>{t.debit ? `₹ ${inr(t.debit)}` : "—"}</td>
                <td className={`px-4 py-3 text-right whitespace-nowrap ${t.credit ? "text-emerald-700 font-semibold" : "text-slate-300"}`}>{t.credit ? `₹ ${inr(t.credit)}` : "—"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap font-bold text-[#0a2a5e]">₹ {inr(t.running_balance)}</td>
                {summary?.has_due_date && <td className="px-4 py-3 whitespace-nowrap text-slate-500">{t.due_date ? fmtDate(t.due_date) : "—"}</td>}
                {summary?.has_due_date && <td className="px-4 py-3 text-right">{t.days_outstanding ? <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">{t.days_outstanding}d</span> : <span className="text-slate-300">—</span>}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {txns && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-1" data-testid="stmt-pagination">
          <div className="text-sm text-slate-600">
            <span className="font-semibold text-slate-800">Page {txns.page} of {totalPages}</span>
            <span className="mx-2 text-slate-300">|</span>
            <span>Total Transactions: <span className="font-semibold text-slate-800">{total}</span></span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(1)} disabled={page === 1 || loading} className="px-2.5 py-1.5 rounded-md border border-slate-200 bg-white text-sm hover:bg-slate-50 disabled:opacity-40">« First</button>
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1 || loading} className="px-3 py-1.5 rounded-md border border-slate-200 bg-white text-sm hover:bg-slate-50 disabled:opacity-40">‹ Prev</button>
            <span className="px-3 py-1.5 text-sm font-semibold text-slate-700">{txns.page} / {totalPages}</span>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading} className="px-3 py-1.5 rounded-md border border-slate-200 bg-white text-sm hover:bg-slate-50 disabled:opacity-40">Next ›</button>
            <button onClick={() => setPage(totalPages)} disabled={page >= totalPages || loading} className="px-2.5 py-1.5 rounded-md border border-slate-200 bg-white text-sm hover:bg-slate-50 disabled:opacity-40">Last »</button>
          </div>
        </div>
      )}
    </div>
  );
}
